import { computed, readonly, Ref } from 'vue';
import MarkdownIt from 'markdown-it';
import htmlToPdfMake from 'html-to-pdfmake';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useToast } from '@/composables/useToast';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';
import { storeToRefs } from 'pinia';

pdfMake.vfs = pdfFonts;

const md = new MarkdownIt({ html: true, linkify: true, typographer: true });

const defaultStyles = {
  h1: { fontSize: 24, bold: true, marginBottom: 8 },
  h2: { fontSize: 22, bold: true, marginBottom: 6 },
  h3: { fontSize: 20, bold: true, marginBottom: 6 },
  h4: { fontSize: 18, bold: true, marginBottom: 6 },
  p: { margin: [0, 4, 0, 8] },
  ul: { margin: [0, 0, 0, 5], marginLeft: 15 },
  ol: { margin: [0, 0, 0, 5], marginLeft: 15 },
  strong: { bold: true },
};

const styleSheet = `h1{font-size:24px;font-weight:bold;margin-bottom:8px;}
h2{font-size:22px;font-weight:bold;margin-bottom:6px;}
h3{font-size:20px;font-weight:bold;margin-bottom:6px;}
h4{font-size:18px;font-weight:bold;margin-bottom:6px;}
p{margin:0 0 8px 0;}
ul,ol{margin:0 0 5px 0;padding-left:15px;}
strong{font-weight:bold;}`;

function htmlToPlain(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent?.trim() ?? '';
}

function stripImageMarkdown(text: string): string {
  return text.replace(/!\[[^\]]*]\([^)]*\)/g, '').trim();
}

async function inlineImages(wrapper: HTMLElement): Promise<void> {
  const imgs = Array.from(wrapper.querySelectorAll<HTMLImageElement>('img'));
  await Promise.all(
    imgs.map(async img => {
      try {
        const resp = await fetch(img.src);
        const blob = await resp.blob();
        await new Promise<void>(res => {
          const reader = new FileReader();
          reader.onload = () => {
            img.src = reader.result as string;
            res();
          };
          reader.readAsDataURL(blob);
        });
      } catch {
        console.warn(`Could not inline ${img.src}`);
      }
    })
  );
}

export interface ExportUtils {
  copyConfluenceMarkdown(): Promise<void>;
  copyMarkdownWithoutImages(): Promise<void>;
  copyHtmlFragment(): Promise<void>;
  downloadPdf(): Promise<void>;
  copyWithVideoLink(): Promise<void>;
}

export function useExportUtils(description: Ref<string | undefined>, displayName: Ref<string | undefined>): ExportUtils {
  const rawHtml = readonly(computed(() => md.render(description.value ?? '')));
  const { success } = useToast();
  const viewerItemStore = useViewerItemStore();
  const { shareLink } = storeToRefs(viewerItemStore);
  const smartActionsStore = useSmartActionsStore();
  const { actionType } = storeToRefs(smartActionsStore);

  function getVideoLink(): string {
    if (!shareLink.value) return '';
    const url = new URL(shareLink.value);
    const sa = actionType?.value || '';
    if (sa) {
      url.searchParams.set('sa', sa);
    }
    return `See the full video: ${url.toString()}`;
  }

  async function copyHtmlFragment(): Promise<void> {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = rawHtml.value;
    await inlineImages(wrapper);

    const htmlContent = `<div>${wrapper.innerHTML}</div><div style="margin-top:1em;">${getVideoLink()}</div>`;

    const htmlFragment = `<style>${styleSheet}</style>${htmlContent}`;
    const plainText = htmlToPlain(wrapper.innerHTML) + `\n\n${getVideoLink()}`;

    try {
      const blobHtml = new Blob([htmlFragment], { type: 'text/html' });
      const blobText = new Blob([plainText], { type: 'text/plain' });
      const item = new ClipboardItem({
        'text/html': blobHtml,
        'text/plain': blobText,
      });
      await navigator.clipboard.write([item]);
    } catch {
      // fallback if ClipboardItem unsupported
      await navigator.clipboard.writeText(plainText);
    }

    success('Content copied to clipboard');
  }

  async function downloadPdf(): Promise<void> {
    success('Preparing PDF…');
    const wrapper = document.createElement('div');
    wrapper.innerHTML = rawHtml.value;
    await inlineImages(wrapper);

    wrapper.querySelectorAll<HTMLImageElement>('img').forEach(img => {
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.style.maxWidth = `700px`;
      img.style.height = 'auto';
    });

    const content = htmlToPdfMake(wrapper.innerHTML, {
      window: globalThis,
      defaultStyles,
      removeExtraBlanks: true,
    });

    pdfMake
      .createPdf({
        content,
        defaultStyle: { fontSize: 11 },
        pageMargins: [20, 20, 20, 20],
      })
      .download(`${displayName.value}.pdf`);
  }

  async function copyMarkdownWithoutImages(): Promise<void> {
    const mdText = stripImageMarkdown(description.value ?? '');
    const result = mdText + `\n\n${getVideoLink()}`;
    await navigator.clipboard.writeText(result);
    success('Content copied to clipboard');
  }

  function addImageBullets(markdown: string): string {
    return markdown
      .split(/\r?\n/)
      .map(line => {
        if (/^\s*-\s*!\[/.test(line)) {
          return line;
        }
        if (/^\s*!\[/.test(line)) {
          return line.replace(/^\s*/, indent => `${indent}- `);
        }
        return line;
      })
      .join('\n');
  }

  function flattenNestedImageLinks(markdown: string): string {
    // Convert [![alt](imgSrc)](linkHref) → ![alt](imgSrc)\n\n[View full-size image](linkHref)
    return markdown.replace(/\[!\[([^\]]*)\]\(([^)]*)\)\]\(([^)]*)\)/g, '![$1]($2)\n\n[View full-size image]($3)');
  }

  async function copyConfluenceMarkdown(): Promise<void> {
    const raw = description.value ?? '';
    const flattened = flattenNestedImageLinks(raw);
    const withBullets = addImageBullets(flattened);
    const result = withBullets + `\n\n${getVideoLink()}`;
    await navigator.clipboard.writeText(result);
    success('Content copied to clipboard');
  }

  async function copyWithVideoLink(): Promise<void> {
    const content = description.value || '';
    const videoLink = getVideoLink();
    const result = content + (videoLink ? `\n\n${videoLink}` : '');

    try {
      await navigator.clipboard.writeText(result);
      success('Content copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }

  return {
    copyConfluenceMarkdown,
    copyMarkdownWithoutImages,
    copyHtmlFragment,
    downloadPdf,
    copyWithVideoLink,
  };
}
